// ─── Voice / video messages and form checks in the gym inbox (member side) ───
// Client-side mirror of ironlog-api/api/_lib/message-media.mjs, so a file that the API
// would refuse is refused before any upload starts, plus the small rules the inbox
// needs (recorder type, signed-link freshness, reply previews). No imports so
// tests/message-media.test.mjs can load this file directly in Node.

export const MEDIA_TYPES = {
  "audio/mp4": { kind: "audio", ext: "m4a" },
  "audio/x-m4a": { kind: "audio", ext: "m4a" },
  "audio/aac": { kind: "audio", ext: "aac" },
  "audio/mpeg": { kind: "audio", ext: "mp3" },
  "audio/webm": { kind: "audio", ext: "webm" },
  "audio/ogg": { kind: "audio", ext: "ogg" },
  "video/mp4": { kind: "video", ext: "mp4" },
  "video/quicktime": { kind: "video", ext: "mov" },
  "video/webm": { kind: "video", ext: "webm" },
};
export const MEDIA_LIMITS = { audio: { bytes: 10_000_000, seconds: 600 }, video: { bytes: 50_000_000, seconds: 300 } };
export const MEDIA_PURPOSES = ["message", "form_check"];
export const EXERCISE_MAX = 80;
export const CAPTION_MAX = 2000;
// A voice note stops itself at the audio limit.
export const RECORD_MAX_SEC = MEDIA_LIMITS.audio.seconds;

// "audio/webm;codecs=opus" → "audio/webm" (the API signs and stores the bare type)
export const baseType = (t) => (typeof t === "string" ? t.split(";")[0].trim().toLowerCase() : "");
export const mediaKind = (t) => (MEDIA_TYPES[baseType(t)] || {}).kind || null;

// Mirror of mediaSpec: { spec } or { error, detail }.
export function mediaSpec(body) {
  const content_type = baseType(body && body.content_type);
  const t = MEDIA_TYPES[content_type];
  if (!t) return { error: "unsupported_media_type", detail: {} };
  const limit = MEDIA_LIMITS[t.kind];
  if (!Number.isSafeInteger(body.bytes) || body.bytes <= 0 || body.bytes > limit.bytes) return { error: "media_too_large", detail: { max_bytes: limit.bytes } };
  let duration_sec = null;
  if (body.duration_sec != null) {
    if (typeof body.duration_sec !== "number" || !Number.isFinite(body.duration_sec) || body.duration_sec <= 0 || body.duration_sec > limit.seconds) return { error: "media_too_long", detail: { max_seconds: limit.seconds } };
    duration_sec = Math.round(body.duration_sec * 10) / 10;
  }
  const purpose = body.purpose == null ? "message" : body.purpose;
  if (!MEDIA_PURPOSES.includes(purpose)) return { error: "invalid_purpose", detail: {} };
  if (purpose === "form_check" && t.kind !== "video") return { error: "invalid_purpose", detail: {} };
  let exercise = null;
  if (body.exercise != null && body.exercise !== "") {
    exercise = String(body.exercise).trim().slice(0, EXERCISE_MAX + 1);
    if (exercise.length > EXERCISE_MAX) return { error: "invalid_exercise", detail: {} };
  }
  return { spec: { content_type, kind: t.kind, ext: t.ext, bytes: body.bytes, duration_sec, purpose, exercise } };
}

// Mirror of mediaLabel: the message body when there is no caption.
export function mediaLabel({ kind, purpose, exercise } = {}, caption) {
  const c = typeof caption === "string" ? caption.trim().slice(0, CAPTION_MAX) : "";
  if (c) return c;
  if (purpose === "form_check") return `🎬 Form check${exercise ? `: ${exercise}` : ""}`;
  return kind === "audio" ? "🎤 Voice message" : "🎬 Video";
}
// The caption of a media message, or "" when its body is only the automatic label.
export const mediaCaption = (m) => (m && m.media && m.body && m.body !== mediaLabel(m.media, "") ? m.body : "");

// Recorder MIME type: the first candidate the browser supports whose base type the API
// accepts (Chrome/Firefox: webm/opus; Safari: mp4/aac). "" lets the browser choose.
const RECORDER_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mp4;codecs=mp4a.40.2", "audio/aac", "audio/ogg;codecs=opus", "audio/ogg"];
export function pickRecorderType(isTypeSupported) {
  if (typeof isTypeSupported !== "function") return "";
  for (const t of RECORDER_CANDIDATES) {
    try { if (isTypeSupported(t) && MEDIA_TYPES[baseType(t)]) return t; } catch {}
  }
  return "";
}

// A picked video file's type: its own when accepted, else guessed from the extension
// (some Android pickers report "" or "application/octet-stream").
export function fileMediaType(file) {
  const t = baseType(file && file.type);
  if (MEDIA_TYPES[t]) return t;
  const ext = String((file && file.name) || "").toLowerCase().split(".").pop();
  return { mp4: "video/mp4", m4v: "video/mp4", mov: "video/quicktime", webm: "video/webm", m4a: "audio/mp4", mp3: "audio/mpeg", aac: "audio/aac", ogg: "audio/ogg" }[ext] || t;
}

// 42 → "0:42", 600 → "10:00", 3725 → "1:02:05"
export function fmtDuration(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}` : `${m}:${String(r).padStart(2, "0")}`;
}
export function fmtBytes(n) {
  const b = Number(n) || 0;
  if (b >= 1_000_000) return `${Math.round(b / 100_000) / 10} MB`;
  if (b >= 1000) return `${Math.round(b / 1000)} KB`;
  return `${b} B`;
}

// Plain-language copy for a refused or failed media message.
export function mediaErrorText(code, detail = {}) {
  switch (code) {
    case "unsupported_media_type": return "This file type can't be sent. Use an MP4, MOV or WebM video.";
    case "media_too_large": return `That file is too large — the limit is ${fmtBytes(detail.max_bytes || 0)}.`;
    case "media_too_long": return `That's too long — the limit is ${fmtDuration(detail.max_seconds || 0)}.`;
    case "invalid_purpose": return "Form checks need a video.";
    case "invalid_exercise": return `Keep the exercise name under ${EXERCISE_MAX} characters.`;
    case "media_unavailable": return "Voice and video messages aren't available right now. Try again later.";
    case "upload_missing": case "upload_mismatch": case "upload_failed": return "The upload didn't finish. Tap Retry.";
    case "already_sent": return "";
    case "invalid_reply_to": return "The message you replied to is no longer available.";
    case "rate_limited": return "Too many uploads — try again in a little while.";
    case "network": return "Couldn't reach the server. Check your connection and tap Retry.";
    default: return code ? `Something went wrong (${code}).` : "Something went wrong.";
  }
}

// Signed links last `expires_in` seconds from when the thread was read. A link is treated
// as expired a minute early so playback never starts on a link about to die.
export const MEDIA_URL_MARGIN_MS = 60_000;
export const mediaExpired = (media, now = Date.now()) =>
  !media || !media.url || !media._fetched_at || now >= media._fetched_at + (Number(media.expires_in) || 3600) * 1000 - MEDIA_URL_MARGIN_MS;

// Merging a re-read message into the one on screen: keep the current signed link while it
// is still fresh (a new link would reload a playing clip every poll), else take the new
// one stamped with `now`.
export function mergeMedia(prev, next, now = Date.now()) {
  if (!next) return prev || null;
  if (prev && prev.id === next.id && !mediaExpired(prev, now)) return { ...next, url: prev.url, _fetched_at: prev._fetched_at };
  return { ...next, _fetched_at: next.url ? now : null };
}

// The quoted line above a reply: { who, text }. `reply` is null when the quoted message
// is gone (the API still returns reply_to).
export function replyPreview(reply, me, otherName) {
  if (!reply) return { who: "", text: "Original message unavailable" };
  const who = reply.sender_email === me ? "You" : (otherName || "Coach");
  const text = String(reply.body || "").replace(/\s+/g, " ").trim() || (reply.kind === "media" ? "Media" : "Message");
  return { who, text: text.length > 140 ? text.slice(0, 139) + "…" : text };
}
// Only real text and media messages can be replied to (not system lines, consent cards
// or messages still sending).
export const canReplyTo = (m) => !!(m && (m.kind === "text" || m.kind === "media") && Number.isSafeInteger(Number(m.id)) && Number(m.id) > 0 && !m.pending);
